import { faAngleLeft, faAngleRight, faAnglesLeft, faAnglesRight, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Dispatch, FC, JSX, ReactNode, SetStateAction, useEffect, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import BTable from 'react-bootstrap/Table';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { persistSortOrder, persistTableFilters } from '../Utilities/SessionStorage';
import { useTranslationUtils } from '../Utilities/TranslationUtils';
import DefaultColumnFilter from './datatable.columnfilter';

// Create a default prop getter
const defaultPropGetter = (item: any) => {};

interface WrapperProps {
    key: string | number;
    data: any;
    children: JSX.Element;
}
const DefaultRowWrapper: FC<WrapperProps> = ({data, children}) => children;

const DefaultCellWrapper: FC<WrapperProps> = ({ data, children }) => children;

interface DataTableProps {
    id?: number | string,
    tableColumns: any[],
    tableData: any[],
    initialState?: any,
    getRowProps?: any,
    getCellProps?: any,
    showPaging?: boolean,
    autoResetPage?: boolean,
    updateData?: any,
    setFilters?: Dispatch<SetStateAction<any[]>>,
    setFilteredRows?: (rows: any[]) => void,
    RowWrapper?: FC<WrapperProps>,
    CellWrapper?: FC<WrapperProps>,
    children?: ReactNode;
    resetFiltersToken?: number;
}

function DataTable({
    id = Math.random(),
    tableColumns,
    tableData,
    initialState,
    getRowProps = defaultPropGetter,
    getCellProps = defaultPropGetter,
    showPaging = true,
    autoResetPage,
    updateData,
    setFilters,
    setFilteredRows,
    RowWrapper = DefaultRowWrapper,
    CellWrapper = DefaultCellWrapper,
    resetFiltersToken,
    children}: DataTableProps) {

    const { tp } = useTranslationUtils();
    const columns = useMemo(() => tableColumns, [tableColumns]);
    const data = useMemo(() => tableData, [tableData]);

    const {
        rows,
        page,
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize, sortBy, filters },
        setHiddenColumns,
        setAllFilters
    } = useTable(
        {
            columns,
            data,
            initialState: initialState,
            defaultColumn: {
                Filter: DefaultColumnFilter
            },
            // When these following properties are to set true, the data needs to be memoized
            autoResetFilters: false,
            autoResetGlobalFilter: false,
            autoResetHiddenColumns: false,
            autoResetSortBy: false,
            autoResetPage: autoResetPage ?? true,
            updateData, // This function is not part of the V7 API but becomes available to cells and columns in the table if added like so
        } as any,
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination
    ) as any; // UseTable hook doesn't document its types very well so avoid the typescript headache and just use any

    useEffect(() => {
        persistSortOrder(`${id}`, sortBy);
    }, [id, sortBy]);

    useEffect(() => {
        persistTableFilters(`${id}`, filters);
    }, [id, filters]);

    useEffect(() => {
        if (setFilters) {
            setFilters(filters);
        }
    }, [filters, setFilters]);

    useEffect(() => {
        if (setFilteredRows) {
            setFilteredRows(rows);
        }
    }, [rows, setFilteredRows]);

    useEffect(() => {
        if (columns.some(col => col.show !== undefined)) {
            setHiddenColumns(columns.filter(col => col.show === false).map(col => col.id ?? col.accessor));
        }
    }, [columns, setHiddenColumns]);

    useEffect(() => {
        if (resetFiltersToken && resetFiltersToken >= 1) {
            setAllFilters([]);
        }
    }, [resetFiltersToken, setAllFilters]);

    function Pagination() {
        return (
            <div className="d-flex justify-content-end align-items-center">
                <div className="me-3">
                    <Form.Select
                        id="pageSize"
                        size="sm"
                        value={pageSize}
                        onChange={e => {
                            setPageSize(Number(e.target.value));
                        }}>
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                {tp("Pagination.ShowXRows", { x: pageSize })}
                            </option>
                        ))}
                    </Form.Select>
                </div>
                <span className="align-items-center me-3">{tp("Pagination.PageXofY", { x: pageIndex + 1, y: pageOptions.length })}</span>
                <div className="pagination">
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        <FontAwesomeIcon icon={faAnglesLeft} />
                    </button>
                    <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                    <button onClick={() => nextPage()} disabled={!canNextPage}>
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                    <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                        <FontAwesomeIcon icon={faAnglesRight} />
                    </button>
                </div>
            </div>
        );
    }

    let displayData = showPaging ? page : rows;
    return (
        <>
            <Row>
                <BTable responsive striped bordered hover size="sm" {...getTableProps()}>
                    <thead>
                        {headerGroups.map((headerGroup: any) => {
                            let { key: rowKey, ...rowProps } = headerGroup.getHeaderGroupProps();
                            return (
                                <tr key={rowKey} {...rowProps} role="row">
                                    {headerGroup.headers.map((column: any) => {
                                        let { key: colKey, ...colProps } = column.getHeaderProps();
                                        let { key: cellKey, ...cellProps } = column.getHeaderProps([column.getSortByToggleProps(), { className: column.className }]);
                                        return (
                                            <th key={colKey} {...colProps} className={column.className} style={{ height: "1px", width: column.width }}> {/*Need height of 1px for h-100% to work even though 1px height get's overriden*/}
                                                <div className="h-100 d-flex flex-column">
                                                    <div key={cellKey} {...cellProps} className="disable-select flex-grow-1 d-flex">
                                                        {column.render('Header')}
                                                        <span className="ms-auto">
                                                            {column.isSorted
                                                                ? column.isSortedDesc
                                                                    ? <FontAwesomeIcon icon={faSortDown} />
                                                                    : <FontAwesomeIcon icon={faSortUp} />
                                                                : <div className="invisible"><FontAwesomeIcon icon={faSortUp} /></div>
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="">{column.canFilter ? column.render('Filter') : null}</div>
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {displayData.map((row: any) => {
                            prepareRow(row);
                            let { key: rowKey, ...rowProps } = row.getRowProps(getRowProps(row));
                            return (
                                <RowWrapper key={rowKey} data={row}>
                                    <tr {...rowProps} role="row">
                                        {row.cells.map((cell: any) => {
                                            let { key: cellKey, ...cellProps } = cell.getCellProps(getCellProps(cell));
                                            return (
                                                <CellWrapper key={cellKey} data={cell}>
                                                    <td {...cellProps}>
                                                        {cell.render('Cell')}
                                                    </td>
                                                </CellWrapper>
                                            );
                                        })}
                                    </tr>
                                </RowWrapper>
                            );
                        })}
                    </tbody>
                </BTable>
            </Row>
            <Row>
                <Col className="float-start">
                    {children}
                </Col>
                {showPaging &&
                    <Col className="float-end">
                        <Pagination />
                    </Col>
                }
            </Row>
        </>
    );
}

export default DataTable;